import React, { useCallback, useEffect, useState } from "react";
import { Ship, UserCog, Loader2, Sunset, Moon, X, FileSpreadsheet, Users, TrendingUp, TrendingDown, Minus, Sailboat } from "lucide-react";
import { toast } from "sonner";
import AliasesModal from "./AliasesModal";
import { Stars, reviewsApi, inputCls, SOURCE_META } from "./shared";

const STAR_COLORS = { 5: "bg-emerald-500/20 text-emerald-300", 4: "bg-lime-500/20 text-lime-300", 3: "bg-amber-500/20 text-amber-300", 2: "bg-orange-500/20 text-orange-300", 1: "bg-rose-500/20 text-rose-300" };
const STAR_TXT = { 5: "text-emerald-300", 4: "text-lime-300", 3: "text-amber-300", 2: "text-orange-300", 1: "text-rose-300" };
const MATRIX_ROWS = [
  ["Toplam", "", null],
  ["GYG", "gyg", "src_gyg"],
  ["TripAdvisor", "ta", "src_tripadvisor"],
  ["Viator", "viator", "src_viator"],
  ["Google", "google", "src_google"],
];
const MATRIX_COLORS = { Toplam: "#CBD5E1", GYG: "#FF5533", TripAdvisor: "#34E0A1", Viator: "#1D8A5E", Google: "#4285F4" };
function StarMatrix({ r }) {
  const rows = MATRIX_ROWS.filter(([, pre, sk]) => pre === "" || (r[sk] || 0) > 0);
  const cell = (label, pre, n) => (pre === "" ? (r[`s${n}`] || 0) : (r[`${pre}_s${n}`] || 0));
  return (
    <table className="text-[11px] border-separate" style={{ borderSpacing: "2px" }} data-testid="star-matrix">
      <thead>
        <tr>
          <th className="text-left font-normal text-slate-500 pr-1"></th>
          {[5, 4, 3, 2, 1].map(n => <th key={n} className={`font-semibold w-7 text-center ${STAR_TXT[n]}`}>{n}★</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map(([label, pre]) => (
          <tr key={label} data-testid={`matrix-row-${label}`}>
            <td className="pr-2 whitespace-nowrap font-semibold" style={{ color: MATRIX_COLORS[label] }}>{label}</td>
            {[5, 4, 3, 2, 1].map(n => {
              const v = cell(label, pre, n);
              return <td key={n} className={`text-center rounded px-1 py-0.5 font-semibold ${v ? (label === "Toplam" ? STAR_COLORS[n] : "bg-slate-700/40 text-slate-200") : "text-slate-600"}`}>{v}</td>;
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Bar({ value, max }) {
  const pct = max ? Math.round((value / max) * 100) : 0;
  return <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden"><div className="h-full bg-blue-500 rounded-full" style={{ width: `${pct}%` }} /></div>;
}

const SEGMENTS = [["", "Hepsi"], ["dinner", "Dinner"], ["sunset", "Sunset"]];
const monthStart = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; };
const today = () => new Date().toISOString().slice(0, 10);

export default function GarsonKarnesi({ onGarsonClick }) {
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [segment, setSegment] = useState("");
  const [by, setBy] = useState("tour");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAliases, setShowAliases] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [trend, setTrend] = useState(null);
  const [trendDays, setTrendDays] = useState(30);
  useEffect(() => { reviewsApi.sourceTrend(trendDays).then(setTrend).catch(() => setTrend(null)); }, [trendDays]);
  const exportKarne = async () => {
    setExporting(true);
    try { await reviewsApi.download("/api/reviews/stats/garson/export", { date_from: from || undefined, date_to: to || undefined, segment: segment || undefined, by }, "garson_karnesi.xlsx"); toast.success("Excel indirildi"); }
    catch { toast.error("Dışa aktarma başarısız"); }
    setExporting(false);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await reviewsApi.garsonStats({ date_from: from || undefined, date_to: to || undefined, segment: segment || undefined, by })); }
    catch { setData(null); }
    setLoading(false);
  }, [from, to, segment, by]);
  useEffect(() => { load(); }, [load]);

  const g = data?.by_garson || [];
  const b = data?.by_boat || [];
  const segs = data?.by_segment || [];
  const srcTotals = data?.by_source || [];
  const max = Math.max(1, ...g.map(x => x.count));

  return (
    <div data-testid="garson-karnesi">
      <AliasesModal open={showAliases} onClose={() => setShowAliases(false)} onChanged={load} />
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="karne-filters">
        <input type="date" value={from} onChange={e => setFrom(e.target.value)} className={inputCls} data-testid="karne-date-from" />
        <span className="text-slate-500 text-xs">—</span>
        <input type="date" value={to} onChange={e => setTo(e.target.value)} className={inputCls} data-testid="karne-date-to" />
        {(from || to) && <button onClick={() => { setFrom(""); setTo(""); }} className="text-xs text-slate-400 hover:text-white flex items-center gap-1" data-testid="karne-clear-dates"><X className="w-3 h-3" /> Tüm zamanlar</button>}
        <select value={by} onChange={e => setBy(e.target.value)} className={inputCls} data-testid="karne-by">
          <option value="tour">Tur tarihine göre</option>
          <option value="review">Yorum tarihine göre</option>
        </select>
        <div className="flex items-center rounded-lg overflow-hidden border border-slate-700 ml-1" data-testid="karne-segment">
          {SEGMENTS.map(([k, label]) => (
            <button key={k} onClick={() => setSegment(k)} data-testid={`karne-segment-${k || "all"}`}
              className={`px-3 py-1.5 text-xs font-semibold flex items-center gap-1 ${segment === k ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}>
              {k === "dinner" && <Moon className="w-3 h-3" />}{k === "sunset" && <Sunset className="w-3 h-3" />}{label}
            </button>
          ))}
        </div>
        <button onClick={() => setShowAliases(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700" data-testid="karne-aliases-btn"><Users className="w-3.5 h-3.5" /> İsim Birleştirme</button>
        <button onClick={exportKarne} disabled={exporting} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-700 hover:bg-emerald-600 text-white" data-testid="karne-export-btn"><FileSpreadsheet className="w-3.5 h-3.5" /> {exporting ? "Hazırlanıyor…" : "Excel'e Aktar"}</button>
        <div className="ml-auto flex items-center gap-3 text-xs text-slate-400" data-testid="karne-segment-totals">
          {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          {segs.map(s => (
            <span key={s.segment} className="flex items-center gap-1">
              {s.segment === "dinner" ? <Moon className="w-3 h-3 text-indigo-300" /> : <Sunset className="w-3 h-3 text-orange-300" />}
              <span className="text-slate-300 capitalize">{s.segment}</span> <span className="text-white font-semibold">{s.count}</span> <span className="text-amber-400">★{s.avg_rating}</span>
            </span>
          ))}
        </div>
      </div>

      {trend?.sources?.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="karne-source-trend">
          <span className="text-xs text-slate-400 font-semibold mr-1 flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> Kaynak yıldız trendi:</span>
          <div className="flex items-center rounded-lg overflow-hidden border border-slate-700" data-testid="karne-trend-days">
            {[[7, "7g"], [30, "30g"], [90, "90g"]].map(([d, label]) => (
              <button key={d} onClick={() => setTrendDays(d)} data-testid={`karne-trend-${d}`}
                className={`px-2 py-1 text-[11px] font-semibold ${trendDays === d ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}>{label}</button>
            ))}
          </div>
          {trend.sources.filter(s => s.cur_avg != null).map(s => {
            const m = SOURCE_META[s.source] || {};
            const up = (s.delta ?? 0) > 0, down = (s.delta ?? 0) < 0;
            const dc = up ? "text-emerald-400" : down ? "text-rose-400" : "text-slate-500";
            const Icon = up ? TrendingUp : down ? TrendingDown : Minus;
            return (
              <span key={s.source} data-testid={`karne-trend-src-${s.source}`}
                title={`${m.label || s.source}: son ${trend.days} gün ★${s.cur_avg} (${s.cur_count}) · önceki ★${s.prev_avg ?? "—"} (${s.prev_count})`}
                className="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg border bg-slate-800/60 border-slate-700">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: m.color }} />
                <span style={{ color: m.color }}>{m.short || s.source}</span>
                <span className="text-amber-400">★{s.cur_avg}</span>
                <span className={`flex items-center gap-0.5 ${dc}`}><Icon className="w-3 h-3" />{s.delta == null ? "—" : `${s.delta > 0 ? "+" : ""}${s.delta}`}</span>
              </span>
            );
          })}
        </div>
      )}

      {srcTotals.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="karne-source-totals">
          <span className="text-xs text-slate-400 font-semibold mr-1">Eşleşmiş yorumların kaynak dağılımı:</span>
          {srcTotals.map(s => {
            const m = SOURCE_META[s.source] || {};
            return (
              <span key={s.source} data-testid={`karne-src-total-${s.source}`}
                className="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg border"
                style={{ backgroundColor: `${m.color}1A`, borderColor: `${m.color}44`, color: m.color }}>
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: m.color }} />
                {m.short || s.source}
                <span className="text-white">{s.count.toLocaleString("tr-TR")}</span>
                <span className="text-amber-400">★{s.avg_rating}</span>
              </span>
            );
          })}
        </div>
      )}

      <div className="space-y-4">
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-x-auto">
          <div className="px-4 py-3 border-b border-slate-700 flex items-center gap-2 text-sm font-semibold text-white"><UserCog className="w-4 h-4 text-emerald-400" /> Garson Karnesi <span className="text-xs text-slate-500 font-normal">{segment ? `· ${segment}` : "· dinner + sunset"} · rezervasyon/pax tur tarihine göre, iptaller hariç</span></div>
          {g.length === 0 ? <div className="p-8 text-center text-sm text-slate-500">{loading ? "Yükleniyor…" : "Bu aralıkta Canlı Sayfa'da garson atanmış rezervasyon yok (garson alanı boş olan satırlar karneye girmez)."}</div> : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-400 text-xs border-b border-slate-700">
                <th className="px-4 py-2">Garson</th>
                <th className="px-3 py-2" title="Dönemde baktığı rezervasyon / kişi">Rezervasyon · Pax</th>
                <th className="px-3 py-2">Yorum</th>
                <th className="px-3 py-2" title="Yorum bırakmayan rezervasyon">Yorumsuz</th>
                <th className="px-3 py-2">Ortalama</th>
                <th className="px-3 py-2">Yıldız Dağılımı</th>
                {!segment && <th className="px-3 py-2" title="Dinner / Sunset / Small yorumları ayrı — ücretlendirme farklı">Dinner / Sunset / Small</th>}
              </tr></thead>
              <tbody>
                {g.map(r => {
                  const pct = r.rez ? Math.round((r.count / r.rez) * 100) : 0;
                  return (
                  <tr key={r.garson} className="border-b border-slate-700/50 hover:bg-slate-700/30 cursor-pointer" onClick={() => onGarsonClick(r.garson, { from, to, segment, by })} data-testid={`garson-stat-${r.garson}`}>
                    <td className="px-4 py-2.5 text-emerald-300 font-medium whitespace-nowrap">{r.garson}{(r.aliases || []).length > 0 && <div className="text-[10px] text-slate-500 font-normal" title={r.aliases.join(", ")} data-testid="garson-aliases-hint">+{r.aliases.length} yazım birleştirildi</div>}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap" data-testid="garson-rez-pax"><span className="text-white font-semibold">{r.rez}</span> <span className="text-slate-500">rez</span> <span className="text-slate-600">·</span> <span className="text-cyan-300 font-semibold">{r.pax}</span> <span className="text-slate-500">pax</span></td>
                    <td className="px-3 py-2.5 w-[140px]"><div className="text-white mb-1">{r.count} <span className="text-[11px] text-slate-500">%{pct}</span></div><Bar value={r.count} max={max} /></td>
                    <td className={`px-3 py-2.5 ${r.no_review ? "text-slate-300" : "text-slate-500"}`} data-testid="garson-no-review">{r.no_review}</td>
                    <td className="px-3 py-2.5"><div className="flex items-center gap-2"><Stars value={Math.round(r.avg_rating || 0)} /><span className="text-white">{r.avg_rating ?? "—"}</span></div></td>
                    <td className="px-3 py-2.5 align-top"><StarMatrix r={r} /></td>
                    {!segment && (
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-500/15 border border-indigo-500/30 text-indigo-200 text-[11px] font-semibold w-fit" data-testid="garson-seg-dinner" title={`Dinner: ${r.dinner} yorum · ${r.rez_dinner} rez`}>
                            <Moon className="w-3 h-3" />Dinner <span className="text-white">{r.dinner}</span>{r.avg_dinner != null && <span className="text-amber-300">★{r.avg_dinner}</span>}
                          </span>
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-orange-500/15 border border-orange-500/30 text-orange-200 text-[11px] font-semibold w-fit" data-testid="garson-seg-sunset" title={`Sunset: ${r.sunset} yorum · ${r.rez_sunset} rez (ayrı ücret)`}>
                            <Sunset className="w-3 h-3" />Sunset <span className="text-white">{r.sunset}</span>{r.avg_sunset != null && <span className="text-amber-300">★{r.avg_sunset}</span>}
                          </span>
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-teal-500/15 border border-teal-500/30 text-teal-200 text-[11px] font-semibold w-fit" data-testid="garson-seg-small" title={`Small: ${r.small} yorum (ayrı ücret)`}>
                            <Sailboat className="w-3 h-3" />Small <span className="text-white">{r.small}</span>{r.avg_small != null && <span className="text-amber-300">★{r.avg_small}</span>}
                          </span>
                        </div>
                      </td>
                    )}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-700 flex items-center gap-2 text-sm font-semibold text-white"><Ship className="w-4 h-4 text-cyan-400" /> Tekne / Ürün Bazında <span className="text-xs text-slate-500 font-normal">· Canlı Sayfa ataması, yoksa ürün adından</span></div>
          {b.length === 0 ? <div className="p-8 text-center text-sm text-slate-500">Veri yok</div> : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4">
              {b.map(r => (
                <div key={`${r.boat_name}-${r.segment}`} className="px-4 py-2.5 flex items-center justify-between text-sm border-b border-slate-700/50" data-testid={`boat-stat-${r.boat_name}`}>
                  <span className={`flex items-center gap-2 ${/belirsiz/.test(r.boat_name) ? "text-cyan-300/60 italic" : "text-cyan-300"}`} title={/belirsiz/.test(r.boat_name) ? "Canlı Sayfa'da tekne atanmamış, ürün adından yalnızca tür belirlenebildi" : ""}>{r.segment === "sunset" ? <Sunset className="w-3.5 h-3.5 text-orange-300" /> : <Moon className="w-3.5 h-3.5 text-indigo-300" />}{r.boat_name}</span>
                  <span className="flex items-center gap-3"><span className="text-slate-300">{r.count} yorum</span><span className="text-amber-400">★ {r.avg_rating}</span></span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
